import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { UserEntrySchema } from './user_entry.ts'
import { AgentProvenanceEntrySchema } from './agent_provenance_entry.ts'
import { InteractionsModelFields } from './interactions_model.ts'
import { InteractionAccountsModelFields } from './interaction_accounts_model.ts'
import { InteractionDealsModelFields } from './interaction_deals_model.ts'
import { InteractionProspectsModelFields } from './interaction_prospects_model.ts'
import {
  TABLE_INTERACTIONS,
  TABLE_INTERACTION_ACCOUNTS,
  TABLE_INTERACTION_DEALS,
  TABLE_INTERACTION_PROSPECTS,
  TABLE_INTERACTION_PARTICIPANTS,
  FIELD_KEY_SEPARATOR,
  FIELD_KEY_PROG
} from '../globals.ts'

const ALIAS_INTERACTIONS = `${TABLE_INTERACTIONS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_INTERACTION_ACCOUNTS = `${TABLE_INTERACTION_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_INTERACTION_DEALS = `${TABLE_INTERACTION_DEALS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_INTERACTION_PROSPECTS = `${TABLE_INTERACTION_PROSPECTS}${FIELD_KEY_SEPARATOR}` as const

const PROG_PARTICIPANTS = `${FIELD_KEY_PROG}${TABLE_INTERACTION_PARTICIPANTS}` as const
// Agent Access 4.5: null unless an agent session created this interaction.
// This schema is .strict(), so the key must be declared here or every
// interactions feed fails validation the moment the RPC starts emitting it.
const PROG_CREATED_PROVENANCE = `${FIELD_KEY_PROG}created_provenance` as const
// Interaction summaries + thread grouping (20261204120100): the content kind
// the summarizer assigned, the queue status (drives the "Summary pending…"
// placeholder) and the thread keys the timeline folds on. All nullish —
// thread_* are absent for singletons and for every non-email type.
const PROG_CONTENT_KIND_KEY = `${FIELD_KEY_PROG}content_kind_key` as const
const PROG_CONTENT_KIND_LABEL = `${FIELD_KEY_PROG}content_kind_label` as const
const PROG_CONTENT_KIND_IS_LOW_VALUE = `${FIELD_KEY_PROG}content_kind_is_low_value` as const
const PROG_SUMMARY_STATUS = `${FIELD_KEY_PROG}summary_status` as const
const PROG_THREAD_GROUP_ID = `${FIELD_KEY_PROG}thread_group_id` as const
const PROG_THREAD_SIZE = `${FIELD_KEY_PROG}thread_size` as const
const PROG_THREAD_LOW_VALUE_COUNT = `${FIELD_KEY_PROG}thread_low_value_count` as const
const PROG_THREAD_LATEST_SUBSTANTIVE_ID = `${FIELD_KEY_PROG}thread_latest_substantive_id` as const
const PROG_THREAD_POSITION = `${FIELD_KEY_PROG}thread_position` as const

const FIELD_ID = `${ALIAS_INTERACTIONS}${InteractionsModelFields.id}` as const
const FIELD_SUBJECT = `${ALIAS_INTERACTIONS}${InteractionsModelFields.subject}` as const
const FIELD_TYPE = `${ALIAS_INTERACTIONS}${InteractionsModelFields.type}` as const
const FIELD_OWNER = `${ALIAS_INTERACTIONS}${InteractionsModelFields.owner}` as const
const FIELD_DATETIME = `${ALIAS_INTERACTIONS}${InteractionsModelFields.datetime}` as const
const FIELD_DESCRIPTION = `${ALIAS_INTERACTIONS}${InteractionsModelFields.description}` as const
const FIELD_NOTES = `${ALIAS_INTERACTIONS}${InteractionsModelFields.notes}` as const
const FIELD_CONTENT_KIND_ID = `${ALIAS_INTERACTIONS}content_kind_id` as const

const FIELD_INTERACTION_ACCOUNTS_ID = `${ALIAS_INTERACTION_ACCOUNTS}${InteractionAccountsModelFields.id}` as const
const FIELD_INTERACTION_ACCOUNTS_ACCOUNT_ID = `${ALIAS_INTERACTION_ACCOUNTS}${InteractionAccountsModelFields.account_id}` as const

const FIELD_INTERACTION_DEALS_ID = `${ALIAS_INTERACTION_DEALS}${InteractionDealsModelFields.id}` as const
const FIELD_INTERACTION_DEALS_DEAL_ID = `${ALIAS_INTERACTION_DEALS}${InteractionDealsModelFields.deal_id}` as const

const FIELD_INTERACTION_PROSPECTS_ID = `${ALIAS_INTERACTION_PROSPECTS}${InteractionProspectsModelFields.id}` as const
const FIELD_INTERACTION_PROSPECTS_PROSPECT_ID = `${ALIAS_INTERACTION_PROSPECTS}${InteractionProspectsModelFields.prospect_id}` as const

export const InteractionRecordSchema = z
  .object({
    // interactions.id is NOT NULL: an interaction without an id is invalid data
    [FIELD_ID]: z.string(),
    [FIELD_SUBJECT]: z.string().nullish(),
    [FIELD_TYPE]: z.string().nullish(),
    [FIELD_OWNER]: UserEntrySchema.nullish(),
    [FIELD_DATETIME]: z.string().nullish(),
    [FIELD_DESCRIPTION]: z.string().nullish(),
    [FIELD_NOTES]: z.string().nullish(),
    [FIELD_INTERACTION_ACCOUNTS_ID]: z.string().nullish(),
    [FIELD_INTERACTION_ACCOUNTS_ACCOUNT_ID]: z.string().nullish(),
    [FIELD_INTERACTION_DEALS_ID]: z.string().nullish(),
    [FIELD_INTERACTION_DEALS_DEAL_ID]: z.string().nullish(),
    [FIELD_INTERACTION_PROSPECTS_ID]: z.string().nullish(),
    [FIELD_INTERACTION_PROSPECTS_PROSPECT_ID]: z.string().nullish(),
    [PROG_PARTICIPANTS]: z.array(UserEntrySchema).nullish(),
    [PROG_CREATED_PROVENANCE]: AgentProvenanceEntrySchema.nullish(),
    [FIELD_CONTENT_KIND_ID]: z.string().nullish(),
    [PROG_CONTENT_KIND_KEY]: z.string().nullish(),
    [PROG_CONTENT_KIND_LABEL]: z.string().nullish(),
    [PROG_CONTENT_KIND_IS_LOW_VALUE]: z.boolean().nullish(),
    [PROG_SUMMARY_STATUS]: z.string().nullish(),
    [PROG_THREAD_GROUP_ID]: z.string().nullish(),
    [PROG_THREAD_SIZE]: z.number().nullish(),
    [PROG_THREAD_LOW_VALUE_COUNT]: z.number().nullish(),
    [PROG_THREAD_LATEST_SUBSTANTIVE_ID]: z.string().nullish(),
    [PROG_THREAD_POSITION]: z.number().nullish()
  })
  .strict()

export const InteractionRecordFields = makeFieldKeys(InteractionRecordSchema)

export type InteractionRecord = z.infer<typeof InteractionRecordSchema>

export function validateInteractionRecord(data: unknown, options: { isArray: true }): InteractionRecord[]
export function validateInteractionRecord(data: unknown, options?: { isArray?: false }): InteractionRecord
export function validateInteractionRecord(
  data: unknown,
  options?: { isArray?: boolean }
): InteractionRecord | InteractionRecord[]
export function validateInteractionRecord(
  data: unknown,
  options?: { isArray?: boolean }
): InteractionRecord | InteractionRecord[] {
  try {
    return validateWithZodSchema(InteractionRecordSchema, data, 'interaction record', options)
  } catch (error) {
    throw new Error(`Interaction record validation failed: ${error}`)
  }
}
